import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { deleteAccount, logout } from '../firebase';
import { useNavigate } from 'react-router-dom';

export default function DeleteAccount() {
  const { currentUser } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const navigate = useNavigate();

  const handleDeleteAccount = async () => {
    if (!currentUser) return;
    
    setError('');
    setLoading(true);
    
    try {
      const result = await deleteAccount(currentUser);
      if (result.success) {
        await logout();
        navigate('/login');
      } else {
        setError(result.error || 'Failed to delete account');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return null; // Or redirect to login
  }

  return (
    <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Delete Account</h2>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {!showConfirmation ? (
        <div>
          <p className="text-gray-600 mb-6">
            Are you sure you want to delete your account? This action cannot be undone. 
            All your data will be permanently removed from our servers.
          </p>
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => setShowConfirmation(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Delete My Account
            </button>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-gray-600 mb-6">
            This will permanently delete your account and all associated data. 
            To confirm, please type <span className="font-bold">DELETE</span> below:
          </p>
          
          <div className="mb-6">
            <input
              type="text"
              id="confirmDelete"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Type DELETE to confirm"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.currentTarget.value === 'DELETE') {
                  handleDeleteAccount();
                }
              }}
            />
          </div>
          
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => setShowConfirmation(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={loading}
              className={`px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                loading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Deleting...' : 'Permanently Delete Account'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
